import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Result, Button } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { AuthProvider, useAuth } from './store/authContext';
import AdminLayout from './components/Layout/AdminLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Analytics from './pages/Analytics';
import Memberships from './pages/Memberships';
import Rebates from './pages/Rebates';
import Withdrawals from './pages/Withdrawals';
import Invites from './pages/Invites';
import Notifications from './pages/Notifications';
import Operations from './pages/Operations';
import CuratedProducts from './pages/CuratedProducts';
import AppVersions from './pages/AppVersions';
import OrderTracking from './pages/OrderTracking';
import Download from './pages/Download';
import TaobaoIntegration from './pages/TaobaoIntegration';
import TaobaoApiUsage from './pages/TaobaoApiUsage';
import RiskControl from './pages/RiskControl';
import HelpCenter from './pages/HelpCenter';
import TicketDetail from './pages/HelpCenter/TicketDetail';
import SystemConfig from './pages/SystemConfig';
import DailyMessages from './pages/DailyMessages';
import LegalDocument from './pages/LegalDocument';
import { adminRouteConfigs, canAccessAdminRoute, findFirstAccessibleRoute } from './config/adminAccess';
import './App.css';

// 路由保护组件
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, admin } = useAuth();

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 50 }}>加载中...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!admin) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// 登录页路由保护（已登录则跳转到仪表盘）
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, admin } = useAuth();

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 50 }}>加载中...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to={findFirstAccessibleRoute(admin)} replace />;
  }

  return children;
};

const RoleRoute = ({ children, allowedRoles }) => {
  const { admin, loading } = useAuth();

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 50 }}>加载中...</div>;
  }

  if (!canAccessAdminRoute(admin, allowedRoles)) {
    const fallbackRoute = findFirstAccessibleRoute(admin);
    return (
      <Result
        status="403"
        title="无权访问"
        subTitle="当前账号没有该页面权限，请联系超级管理员开通。"
        extra={
          <Button type="primary" href={fallbackRoute}>
            返回可访问页面
          </Button>
        }
      />
    );
  }

  return children;
};

const renderProtectedElement = (element, allowedRoles) => (
  <RoleRoute allowedRoles={allowedRoles}>
    {element}
  </RoleRoute>
);

function AppRoutes() {
  const defaultRoute = (
    <AuthDefaultRedirect />
  );

  return (
    <Routes>
      <Route path="/" element={<Download />} />
      <Route path="/download" element={<Download />} />
      <Route path="/privacy" element={<LegalDocument documentKey="privacyPolicy" />} />
      <Route path="/personal-info-list" element={<LegalDocument documentKey="personalInfoList" />} />
      <Route path="/user-agreement" element={<LegalDocument documentKey="userAgreement" />} />
      <Route path="/sdk-list" element={<LegalDocument documentKey="sdkList" />} />
      <Route path="/account-deletion" element={<LegalDocument documentKey="accountDeletion" />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      {adminRouteConfigs.map((item) => (
        <Route
          key={`legacy-${item.key}`}
          path={item.key.replace('/admin/', '/')}
          element={<Navigate to={item.key} replace />}
        />
      ))}
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <AdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={defaultRoute} />
        <Route path="dashboard" element={renderProtectedElement(<Dashboard />, adminRouteConfigs.find((item) => item.key === '/admin/dashboard')?.roles)} />
        <Route path="analytics" element={renderProtectedElement(<Analytics />, ['SUPER_ADMIN'])} />
          <Route path="users" element={renderProtectedElement(<Users />, adminRouteConfigs.find((item) => item.key === '/admin/users')?.roles)} />
        <Route path="risk-control" element={renderProtectedElement(<RiskControl />, adminRouteConfigs.find((item) => item.key === '/admin/risk-control')?.roles)} />
        <Route path="help-center" element={renderProtectedElement(<HelpCenter />, adminRouteConfigs.find((item) => item.key === '/admin/help-center')?.roles)} />
        <Route path="help-center/tickets/:ticketId" element={renderProtectedElement(<TicketDetail />, adminRouteConfigs.find((item) => item.key === '/admin/help-center')?.roles)} />
        <Route path="memberships" element={renderProtectedElement(<Memberships />, adminRouteConfigs.find((item) => item.key === '/admin/memberships')?.roles)} />
        <Route path="rebates" element={renderProtectedElement(<Rebates />, adminRouteConfigs.find((item) => item.key === '/admin/rebates')?.roles)} />
        <Route path="withdrawals" element={renderProtectedElement(<Withdrawals />, adminRouteConfigs.find((item) => item.key === '/admin/withdrawals')?.roles)} />
        <Route path="invites" element={renderProtectedElement(<Invites />, adminRouteConfigs.find((item) => item.key === '/admin/invites')?.roles)} />
        <Route path="notifications" element={renderProtectedElement(<Notifications />, adminRouteConfigs.find((item) => item.key === '/admin/notifications')?.roles)} />
        <Route path="operations" element={renderProtectedElement(<Operations />, adminRouteConfigs.find((item) => item.key === '/admin/operations')?.roles)} />
        <Route path="curated-products" element={renderProtectedElement(<CuratedProducts />, adminRouteConfigs.find((item) => item.key === '/admin/curated-products')?.roles)} />
        <Route path="app-versions" element={renderProtectedElement(<AppVersions />, adminRouteConfigs.find((item) => item.key === '/admin/app-versions')?.roles)} />
        <Route path="order-tracking" element={renderProtectedElement(<OrderTracking />, adminRouteConfigs.find((item) => item.key === '/admin/order-tracking')?.roles)} />
        <Route path="system-config" element={renderProtectedElement(<SystemConfig />, adminRouteConfigs.find((item) => item.key === '/admin/system-config')?.roles)} />
        <Route path="daily-messages" element={renderProtectedElement(<DailyMessages />, adminRouteConfigs.find((item) => item.key === '/admin/daily-messages')?.roles)} />
        <Route path="taobao-api-usage" element={renderProtectedElement(<TaobaoApiUsage />, adminRouteConfigs.find((item) => item.key === '/admin/taobao-api-usage')?.roles)} />
        <Route path="taobao-integration" element={renderProtectedElement(<TaobaoIntegration />, adminRouteConfigs.find((item) => item.key === '/admin/taobao-integration')?.roles)} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AuthDefaultRedirect() {
  const { isAuthenticated, admin, loading } = useAuth();

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 50 }}>加载中...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={findFirstAccessibleRoute(admin)} replace />;
}

function App() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#ff2442',
          colorLink: '#ff2442',
          colorInfo: '#ff2442',
          colorBgLayout: '#fff5f6',
          borderRadius: 8,
        },
        components: {
          Layout: {
            bodyBg: '#fff5f6',
            siderBg: '#b90026',
            triggerBg: '#8a1024',
            headerBg: '#ffffff',
          },
          Menu: {
            darkItemBg: '#b90026',
            darkSubMenuItemBg: '#b90026',
            darkItemColor: 'rgba(255, 255, 255, 0.82)',
            darkItemHoverBg: '#d80f2f',
            darkItemSelectedBg: '#ff2442',
            darkItemSelectedColor: '#ffffff',
          },
          Button: {
            primaryShadow: '0 10px 24px rgba(255, 36, 66, 0.24)',
          },
        },
      }}
    >
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
